import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Box,
  IconButton,
  Tooltip,
  useTheme,
  Snackbar,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import React, { useState, useEffect, useCallback } from "react";
import Header from "../../components/Header";
import { tokens } from "../../theme";
import MuiAlert from "@mui/material/Alert";
import { useNavigate } from "react-router-dom";

import { authFetch } from "../../Utils/apiClient";

// Orders come from /api/orders/admin: one row per order, with each seller's
// part (status, driver) summarised.

export const statusColor = (status) => {
  const s = String(status || "").toLowerCase();
  if (s === "processing" || s === "pending") return "warning";
  if (s === "shipped" || s === "ready to pickup") return "info";
  if (s === "delivered") return "success";
  if (s === "refunded" || s === "cancelled" || s === "expired") return "default";
  return "primary";
};

export const paymentColor = (status) =>
  ({
    paid: "success",
    partially_refunded: "warning",
    refunded: "default",
    review: "error",
    disputed: "error",
    unpaid: "default",
    expired: "default",
  }[status] || "default");

export const paymentLabel = (status) =>
  ({
    paid: "Paid",
    partially_refunded: "Part refunded",
    refunded: "Refunded",
    review: "Check payment",
    disputed: "Disputed",
    unpaid: "Unpaid",
    expired: "Expired",
  }[status] || status || "—");

const money = (cents) => `$${(Number(cents || 0) / 100).toFixed(2)}`;
const PAID_STATES = ["paid", "partially_refunded", "refunded", "review", "disputed"];
const MAX_PAGES = 20;

const OrderManagement = () => {
  const [orders, setOrders] = useState([]);
  const [view, setView] = useState("paid");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");

  const formatDate = (value) =>
    new Date(value).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  const loadOrders = useCallback(async () => {
    setLoading(true);
    try {
      const all = [];
      for (let page = 1; page <= MAX_PAGES; page += 1) {
        const response = await authFetch(`/api/orders/admin?page=${page}`);
        const result = await response.json();
        if (!response.ok || !result.status) throw new Error(result.message || "Failed to fetch orders");
        all.push(...result.orders);
        if (all.length >= result.total || result.orders.length < result.pageSize) break;
      }
      setOrders(all);
    } catch (error) {
      setAlertMessage(error.message || "Failed to fetch orders");
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const needsAttention = (o) =>
    ["review", "disputed"].includes(o.paymentStatus) ||
    (o.deliveryMethod !== "pickup" &&
      ["paid", "partially_refunded"].includes(o.paymentStatus) &&
      o.parts.some((p) => ["Processing", "Shipped"].includes(p.status) && (!p.assignee || p.driverStatus === "declined")));

  const rows = orders
    .filter((o) =>
      view === "all" ? true : view === "attention" ? needsAttention(o) : PAID_STATES.includes(o.paymentStatus)
    )
    .map((o) => ({
      ...o,
      buyerName: o.buyer?.name || o.buyer?.email || `User ${o.buyer?.id ?? ""}`,
      total: o.totalCents,
      partsSummary: o.parts.map((p) => p.status).join(" · "),
      date: o.createdAt,
    }));

  const columns = [
    { field: "id", headerName: "Order", width: 90, renderCell: (p) => `#${p.value}` },
    { field: "buyerName", headerName: "Buyer", flex: 1, minWidth: 150 },
    { field: "total", headerName: "Total", width: 110, renderCell: (p) => money(p.value) },
    {
      field: "refundedCents",
      headerName: "Refunded",
      width: 100,
      renderCell: (p) => (p.value ? money(p.value) : "—"),
    },
    {
      field: "paymentStatus",
      headerName: "Payment",
      width: 140,
      renderCell: (p) => <Chip label={paymentLabel(p.value)} color={paymentColor(p.value)} size="small" />,
    },
    {
      field: "status",
      headerName: "Order status",
      width: 150,
      renderCell: (p) => <Chip label={p.value} color={statusColor(p.value)} size="small" />,
    },
    {
      field: "partsSummary",
      headerName: "Sellers' parts",
      flex: 1,
      minWidth: 180,
      renderCell: (p) => (
        <Box display="flex" gap={0.5} flexWrap="wrap">
          {p.row.parts.map((part) => (
            <Chip
              key={part.id}
              size="small"
              variant="outlined"
              color={statusColor(part.status)}
              label={`${part.status}${part.assignee ? "" : p.row.deliveryMethod === "pickup" ? "" : " · no driver"}`}
            />
          ))}
        </Box>
      ),
    },
    {
      field: "deliveryMethod",
      headerName: "Method",
      width: 100,
      renderCell: (p) => (p.value === "pickup" ? "Pickup" : "Delivery"),
    },
    { field: "date", headerName: "Ordered", width: 170, renderCell: (p) => formatDate(p.value) },
    {
      field: "actions",
      headerName: "",
      width: 70,
      sortable: false,
      renderCell: (params) => (
        <Tooltip title="Open order">
          <IconButton onClick={() => navigate(`/order/view/${params.row.id}`)}>
            <VisibilityIcon />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  const attentionCount = orders.filter(needsAttention).length;

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb="20px">
        <Header title="Order Management" subtitle="Each order shows every seller's part, its status and driver." />
        <ToggleButtonGroup size="small" exclusive value={view} onChange={(_, v) => v && setView(v)}>
          <ToggleButton value="paid">Paid</ToggleButton>
          <ToggleButton value="attention">Needs action{attentionCount ? ` (${attentionCount})` : ""}</ToggleButton>
          <ToggleButton value="all">All checkouts</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box
        m="10px 0 0 0"
        height="65vh"
        sx={{
          "& .MuiDataGrid-root": { border: "none", fontSize: "14px" },
          "& .MuiDataGrid-cell": { borderBottom: "none" },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.greenAccent[700],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": { backgroundColor: colors.primary[400] },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.greenAccent[700],
          },
        }}
      >
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          components={{ Toolbar: GridToolbar }}
          pageSize={25}
          rowsPerPageOptions={[10, 25, 50]}
          getRowId={(row) => row.id}
          onRowDoubleClick={(params) => navigate(`/order/view/${params.row.id}`)}
          initialState={{ sorting: { sortModel: [{ field: "id", sort: "desc" }] } }}
        />
      </Box>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={5000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MuiAlert onClose={() => setOpenSnackbar(false)} severity="error" elevation={6} variant="filled" style={{ color: "white" }}>
          {alertMessage}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
};

export default OrderManagement;
